import SearchBar from 'components/common/SearchBar'
import SearchDropdown from 'components/common/SearchDropdown'
import { CategoryTopic } from 'interface/CategoryTopic'
import React, { useCallback, useEffect, useMemo } from 'react'
import styles from 'styles/components/CategorySortCard.module.sass'

type SortId = 'date-desc' | 'date-asc' | 'title-asc' | 'title-desc';

type Prepared = {
    ref: CategoryTopic;
    titleLC: string;
    descLC: string;
    dateKeyAsc: number;
    dateKeyDesc: number;
}

/**
 * Get the month number from a month string.
 *
 * @param month - The month string to get the number for.
 * @returns The month number (1-12) or null if invalid.
 */
const MONTH_MAP : Record<string, number> = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12
}

/**
 * Parses a month string into a month number.
 *
 * @param month - The month string to parse.
 * @returns The month number (1-12) or null if invalid.
 */
const parseMonth = (month?: string): number | null => {
    if (!month) return null;
    return MONTH_MAP[month.trim().toLowerCase()] ?? null;
}

/**
 * Get the date key for a report.
 *
 * @param report - The report to get the date key for.
 * @param defaultMonth - The default month to use if the report month is not available.
 * @returns The date key for the report.
 */
const getDateKey = (report: CategoryTopic, defaultMonth: number = 1) => {
    const y = report.year;
    const year: number = typeof y === 'number' && Number.isFinite(y) ? y : 0;
    const monthValue: string | undefined = typeof report.meta.month === 'string' ? report.meta.month : undefined;
    const m: number = parseMonth(monthValue) ?? defaultMonth;
    return year * 100 + m;
}

const sortOptions = [
    { id: 'date-desc', name: 'Date Published (Descending)' },
    { id: 'date-asc', name: 'Date Published (Ascending)' },
    { id: 'title-asc', name: 'Title (A-Z)' },
    { id: 'title-desc', name: 'Title (Z-A)' },
]

interface CategorySortCardProps {
    reports: CategoryTopic[];
    setFilteredReports: React.Dispatch<React.SetStateAction<CategoryTopic[]>>;
}

/**
 * CategorySortCard component for sorting and filtering reports.
 *
 * @param param0 - The props for the component.
 * @returns The rendered component.
 * 
 * @example
 * <CategorySortCard reports={reports} setFilteredReports={setFilteredReports} />
 */
const CategorySortCard: React.FC<CategorySortCardProps> = ({ reports, setFilteredReports }) => {
    const [sortId, setSortId] = React.useState<SortId>('date-desc');
    const [query, setQuery] = React.useState<string>('');

    const prepared = useMemo<Prepared[]>(() => {
        return reports.map((report) => ({
            ref: report,
            titleLC: report.title.toLowerCase(),
            descLC: report.description?.toLowerCase() ?? '',
            dateKeyAsc: getDateKey(report, 1),
            dateKeyDesc: getDateKey(report, 12),
        }))
    }, [reports])

    const collator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }), []);

    const apply = useCallback((q: string, currentSort: SortId) => {
        const qq = (q ?? '').trim().toLowerCase();

        const base = qq
        ? prepared.filter((p) => p.titleLC.includes(qq) || p.descLC.includes(qq))
        : prepared;

        const arr = base.slice();

        switch (currentSort) {
        case 'date-desc': {
            arr.sort((a, b) => {
            const diff = b.dateKeyDesc - a.dateKeyDesc;
            return diff !== 0 ? diff : collator.compare(a.ref.title ?? '', b.ref.title ?? '');
            });
            break;
        }
        case 'date-asc': {
            arr.sort((a, b) => {
            const diff = a.dateKeyAsc - b.dateKeyAsc;
            return diff !== 0 ? diff : collator.compare(a.ref.title ?? '', b.ref.title ?? '');
            });
            break;
        }
        case 'title-asc':
            arr.sort((a, b) => collator.compare(a.ref.title ?? '', b.ref.title ?? ''));
            break;
        case 'title-desc':
            arr.sort((a, b) => collator.compare(b.ref.title ?? '', a.ref.title ?? ''));
            break;
        }

        setFilteredReports(arr.map((p) => p.ref));
        },
    [prepared, collator, setFilteredReports])

    useEffect(() => {
        apply(query, sortId)
    }, [query, sortId, apply])

    const runSearchReports = (q: string) => {
        setQuery(q);
        apply(q, sortId);
    }

    const handleSort = (nextId: SortId) => {
        setSortId(nextId);
        apply(query, nextId);
    }

    return (
        <div className={styles['category-sort-card']}>
            <div className={styles['sort-header']}>
                <div className={styles['search-container']}>
                    <p className={styles['label']}>Search</p>
                    <SearchBar
                        placeholder={'Search for report'}
                        onSearch={(query: string) => {
                            runSearchReports(query);
                        }}
                        primaryColor={'#005581'}
                    />
                </div>
                <div className={styles['sort-container']}>
                    <p className={styles['label']}>Search</p>
                    <SearchDropdown
                        options={sortOptions}
                        onSelect={(id) => handleSort(id as SortId)}
                        buttonName={'Sort'}
                        placeholder={'Select sort option'}
                        buttonColor={'#005581'}
                        customStyles={{ maxWidth: '400px' }}
                    />
                </div>
            </div>
        </div>
    )
}

export default CategorySortCard